import * as monaco from 'monaco-editor';

export interface CatalogItem {
    mainFile: string;
    category?: string;
    description?: string;
    otherEditbleFiles?: string[];
    otherEditableFiles?: string[];
    extraFiles?: string[];
}

export interface Catalog {
    rootUrl?: string;
    examples: Record<string, CatalogItem>;
}

export interface Example {
    mainFile: string;
    rawMainUrl?: string;
    category?: string;
    description?: string;
    files: Record<string, string>;
    fileUrls?: Record<string, string>;
}

const DEFAULT_ROOT_URL = 'https://raw.githubusercontent.com/ginga-org-br/gingaf/refs/heads/main/examples/';
const FALLBACK_CATALOG_URL = `${DEFAULT_ROOT_URL}catalog.json`;
let catalogRootUrl = DEFAULT_ROOT_URL;

const examples: Record<string, Example> = {};
let catalogLoaded = false;
let catalogLoadPromise: Promise<void> | null = null;

function createExampleFromCatalogItem(key: string, item: CatalogItem, rootUrl: string): Example {
    const mainFile = item.mainFile;
    const relDir = mainFile.includes('/') ? mainFile.substring(0, mainFile.lastIndexOf('/')) : '';
    const rawMainUrl = `${rootUrl}${mainFile}`;
    const files: Record<string, string> = {
        [mainFile]: ''
    };
    const fileUrls: Record<string, string> = {
        [mainFile]: rawMainUrl
    };

    const extraFilesList = item.otherEditbleFiles || item.otherEditableFiles || item.extraFiles;
    if (extraFilesList && Array.isArray(extraFilesList)) {
        for (const extra of extraFilesList) {
            const fullRel = (!relDir || extra.startsWith(relDir + '/')) ? extra : `${relDir}/${extra}`;
            const extraUrl = `${rootUrl}${fullRel}`;
            files[fullRel] = '';
            fileUrls[fullRel] = extraUrl;
        }
    }

    return {
        mainFile,
        rawMainUrl,
        category: item.category,
        description: item.description,
        files,
        fileUrls
    };
}

export async function fetchCatalog(): Promise<Catalog> {
    const candidateUrls: string[] = [];
    if (typeof window !== 'undefined') {
        if (window.location.origin) {
            candidateUrls.push(`${window.location.origin}/examples/catalog.json`);
        }
        candidateUrls.push('/examples/catalog.json');
    }
    candidateUrls.push(FALLBACK_CATALOG_URL);

    for (const url of candidateUrls) {
        try {
            const res = await fetch(url);
            if (res.ok) {
                const data = await res.json();
                if (data && data.examples) {
                    let rootUrl = data.rootUrl;
                    if (!rootUrl) {
                        if (url.startsWith('http://') || url.startsWith('https://')) {
                            try {
                                const parsed = new URL(url);
                                if (typeof window !== 'undefined' && window.location.origin && parsed.origin === window.location.origin) {
                                    rootUrl = parsed.pathname.substring(0, parsed.pathname.lastIndexOf('/') + 1);
                                } else {
                                    rootUrl = url.substring(0, url.lastIndexOf('/') + 1);
                                }
                            } catch (_) {
                                rootUrl = url.substring(0, url.lastIndexOf('/') + 1);
                            }
                        } else if (url.startsWith('/')) {
                            rootUrl = url.substring(0, url.lastIndexOf('/') + 1);
                        } else {
                            rootUrl = DEFAULT_ROOT_URL;
                        }
                    }
                    if (rootUrl.startsWith('/') && typeof window !== 'undefined' && window.location.origin) {
                        rootUrl = `${window.location.origin}${rootUrl}`;
                    }
                    catalogRootUrl = rootUrl;
                    return {
                        rootUrl,
                        examples: data.examples
                    };
                }
            }
        } catch (_) { }
    }

    const defaultRoot = (typeof window !== 'undefined' && window.location.origin && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.port))
        ? `${window.location.origin}/examples/`
        : DEFAULT_ROOT_URL;
    catalogRootUrl = defaultRoot;

    return {
        rootUrl: defaultRoot,
        examples: {}
    };
}

export async function ensureCatalogLoaded(): Promise<void> {
    if (catalogLoaded) return;
    if (catalogLoadPromise) return catalogLoadPromise;

    catalogLoadPromise = (async () => {
        const catalog = await fetchCatalog();
        catalogRootUrl = catalog.rootUrl || DEFAULT_ROOT_URL;
        for (const [key, item] of Object.entries(catalog.examples || {})) {
            examples[key] = createExampleFromCatalogItem(key, item, catalogRootUrl);
        }
        if (Object.keys(examples).length === 0) {
            examples['video'] = {
                mainFile: 'video.ncl',
                rawMainUrl: `${DEFAULT_ROOT_URL}video.ncl`,
                category: 'media',
                description: 'Video media presentation example',
                files: { 'video.ncl': '' },
                fileUrls: { 'video.ncl': `${DEFAULT_ROOT_URL}video.ncl` }
            };
        }
        catalogLoaded = true;
    })();

    return catalogLoadPromise;
}

(self as any).MonacoEnvironment = {
    getWorkerUrl(_workerId: string, _label: string) {
        const prefix = typeof window !== 'undefined' && window.location.pathname.includes('/examples/') ? '../' : './';
        return prefix + 'editor.worker.js';
    }
};

export function isEditableFile(fileName: string): boolean {
    return fileName.endsWith('.ncl') ||
        fileName.endsWith('.xml') ||
        fileName.endsWith('.lua') ||
        fileName.endsWith('.html') ||
        fileName.endsWith('.htm') ||
        fileName.endsWith('.json') ||
        fileName.endsWith('.js') ||
        fileName.endsWith('.css') ||
        fileName.endsWith('.txt');
}

export function getEditorLanguage(fileName: string): string {
    if (fileName.endsWith('.lua')) return 'lua';
    if (fileName.endsWith('.html') || fileName.endsWith('.htm')) return 'html';
    if (fileName.endsWith('.json')) return 'json';
    if (fileName.endsWith('.js')) return 'javascript';
    if (fileName.endsWith('.css')) return 'css';
    return 'xml';
}

export function resolveFileUrl(url: string): string {
    return url;
}

export function getExampleUrl(example: Example): string {
    if (example.rawMainUrl && (example.rawMainUrl.startsWith('http://') || example.rawMainUrl.startsWith('https://'))) {
        return example.rawMainUrl;
    }
    const mainFile = example.mainFile;
    if (typeof window !== 'undefined' && window.location.origin) {
        if (example.rawMainUrl) {
            try {
                return new URL(example.rawMainUrl, window.location.href).href;
            } catch (_) { }
        }
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.port) {
            return `${window.location.origin}/examples/${mainFile}`;
        }
    }
    return `${DEFAULT_ROOT_URL}${mainFile}`;
}

export async function loadExampleFiles(example: Example): Promise<void> {
    for (const fileName of Object.keys(example.files)) {
        if (isEditableFile(fileName) && (!example.files[fileName] || example.files[fileName] === '')) {
            const url = example.fileUrls?.[fileName] || `${catalogRootUrl}${fileName}`;
            const resolved = resolveFileUrl(url);
            const localCandidates: string[] = [];
            if (typeof window !== 'undefined' && window.location.origin) {
                localCandidates.push(`${window.location.origin}/examples/${fileName}`);
            }
            localCandidates.push(`/examples/${fileName}`);
            const remoteFallback = `${DEFAULT_ROOT_URL}${fileName}`;
            const fetchList = [resolved, url, ...localCandidates, remoteFallback];
            for (const candidate of fetchList) {
                if (!candidate) continue;
                try {
                    const res = await fetch(candidate);
                    if (res.ok) {
                        const text = await res.text();
                        if (fileName.endsWith('.ncl') || fileName.endsWith('.lua') || fileName.endsWith('.json')) {
                            if (text.trim().startsWith('<!DOCTYPE') || text.trim().startsWith('<html') || text.trim().startsWith('<HTML')) {
                                continue;
                            }
                        }
                        example.files[fileName] = text;
                        break;
                    }
                } catch (_) { }
            }
        }
    }
}

export function getAllFiles(example: Example): Record<string, string> {
    const result: Record<string, string> = {};
    if (example.fileUrls) {
        for (const [k, v] of Object.entries(example.fileUrls)) {
            result[k] = resolveFileUrl(v);
        }
    }
    const relDir = example.mainFile.includes('/') ? example.mainFile.substring(0, example.mainFile.lastIndexOf('/')) : '';
    for (const [k, v] of Object.entries(example.files)) {
        result[k] = v;
        if (k.includes('/')) {
            const base = k.split('/').pop()!;
            result[base] = v;
        } else if (relDir) {
            result[`${relDir}/${k}`] = v;
        }
    }
    if (example.mainFile && example.files[example.mainFile] !== undefined) {
        result[example.mainFile] = example.files[example.mainFile];
        if (example.rawMainUrl) {
            result[example.rawMainUrl] = example.files[example.mainFile];
        }
    }
    return result;
}

export function getEffectiveSearch(): string {
    if (typeof window === 'undefined') return '';
    if (window.location.search) return window.location.search;
    try {
        const scriptEl = (document.currentScript || document.querySelector('script[src*="bundle.js"], script[src*="main.ts"]')) as HTMLScriptElement;
        if (scriptEl && scriptEl.src && scriptEl.src.includes('?')) {
            return scriptEl.src.slice(scriptEl.src.indexOf('?'));
        }
    } catch (_) { }
    return '';
}

export function resolvePlayerBaseUrl(playerParam: string | null): string {
    const isInExamples = typeof window !== 'undefined' && window.location.pathname.includes('/examples/');
    let playerBaseUrl = isInExamples ? '../gingaf-web/index.html' : 'gingaf-web/index.html';
    const isHostedEnv = typeof process !== 'undefined' && (process as any).env ? (process as any).env.VITE_USE_HOSTED_PLAYER === 'true' : false;
    const useHosted = playerParam === 'hosted' || (playerParam !== 'local' && isHostedEnv);
    if (useHosted) {
        playerBaseUrl = 'https://ginga-org-br.github.io/gingaf/playground/gingaf-web/index.html';
    }
    return playerBaseUrl;
}

export async function resolveTargetExample(appParam: string | null): Promise<Example> {
    await ensureCatalogLoaded();

    if (!appParam) {
        return examples['video'] || Object.values(examples)[0];
    }

    const cleanParam = appParam.split('?')[0];
    const extractedFileName = cleanParam.split('/').pop() || cleanParam;
    const extractedKey = extractedFileName.replace(/\.[^/.]+$/, '');

    if (examples[appParam]) return examples[appParam];
    if (examples[cleanParam]) return examples[cleanParam];

    if (appParam.startsWith('http://') || appParam.startsWith('https://')) {
        try {
            const res = await fetch(appParam);
            if (res.ok) {
                const text = await res.text();
                const fileName = extractedFileName || 'app.ncl';
                const remoteExample: Example = {
                    mainFile: fileName,
                    rawMainUrl: appParam,
                    category: 'remote',
                    description: 'Remote App',
                    files: { [fileName]: text },
                    fileUrls: { [fileName]: appParam }
                };
                examples[appParam] = remoteExample;
                return remoteExample;
            }
        } catch (e) {
            console.warn('[ginga-node] Could not fetch remote app URL:', e);
        }

        const fileName = extractedFileName || 'app.ncl';
        const remoteExample: Example = {
            mainFile: fileName,
            rawMainUrl: appParam,
            category: 'remote',
            description: 'Remote App',
            files: {},
            fileUrls: { [fileName]: appParam }
        };
        examples[appParam] = remoteExample;
        return remoteExample;
    }

    if (cleanParam.startsWith('examples/')) {
        const sub = cleanParam.replace(/^examples\//, '');
        if (examples[sub]) return examples[sub];
    }
    if (cleanParam === 'sbtvd/main.ncl' || cleanParam === 'sbtvd') return examples['sbtvd'];
    if (cleanParam === 'multiuser_profile/main.ncl' || cleanParam === 'multiuser_profile') return examples['multiuser_profile'];
    if (cleanParam === 'multiuser_current/main.ncl' || cleanParam === 'multiuser_current') return examples['multiuser_current'];
    if (examples[extractedFileName]) return examples[extractedFileName];
    if (examples[extractedKey]) return examples[extractedKey];

    for (const key of Object.keys(examples)) {
        if (
            key === cleanParam ||
            (examples[key].rawMainUrl && examples[key].rawMainUrl.endsWith(cleanParam)) ||
            examples[key].mainFile === cleanParam ||
            examples[key].mainFile === extractedFileName ||
            examples[key].mainFile === appParam
        ) {
            return examples[key];
        }
    }

    const relativePath = cleanParam.startsWith('examples/') ? cleanParam.slice(9) : cleanParam;
    const candidateRawUrl = `${catalogRootUrl}${relativePath}`;
    try {
        const resolved = resolveFileUrl(candidateRawUrl);
        let res = await fetch(resolved);
        if (!res.ok && resolved !== candidateRawUrl) {
            res = await fetch(candidateRawUrl);
        }
        if (res.ok) {
            const text = await res.text();
            const fileName = extractedFileName || 'app.ncl';
            const dynamicExample: Example = {
                mainFile: fileName,
                rawMainUrl: candidateRawUrl,
                category: 'general',
                description: fileName,
                files: { [fileName]: text },
                fileUrls: { [fileName]: candidateRawUrl }
            };
            examples[cleanParam] = dynamicExample;
            examples[extractedKey] = dynamicExample;
            return dynamicExample;
        }
    } catch (_) { }

    return examples['video'] || Object.values(examples)[0];
}

function syncIframeStorage(win: any, example: Example) {
    try {
        const allFiles = getAllFiles(example);
        const filesJson = JSON.stringify(allFiles);
        const appUrl = getExampleUrl(example);
        if (win && win.sessionStorage) {
            win.sessionStorage.setItem('GINGA_APP', appUrl);
            win.sessionStorage.setItem('GINGA_PLAYGROUND_MAIN', example.mainFile);
            win.sessionStorage.setItem('GINGA_PLAYGROUND_FILES', filesJson);
            win.GingaApp = {
                appPath: appUrl,
                currentApp: appUrl,
                files: allFiles
            };
        }
        if (typeof window !== 'undefined') {
            window.sessionStorage.setItem('GINGA_APP', appUrl);
            window.sessionStorage.setItem('GINGA_PLAYGROUND_MAIN', example.mainFile);
            window.sessionStorage.setItem('GINGA_PLAYGROUND_FILES', filesJson);
            (window as any).GingaApp = {
                appPath: appUrl,
                currentApp: appUrl,
                files: allFiles
            };
        }
    } catch (_) { }
}

export function getEffectiveMode(): 'player' | 'playground' | 'playgroundSingle' | 'single' {
    const search = getEffectiveSearch();
    const urlParams = new URLSearchParams(search);
    const urlMode = urlParams.get('mode');
    if (urlMode === 'player' || urlMode === 'playground' || urlMode === 'playgroundSingle' || urlMode === 'single') {
        return urlMode as any;
    }

    const bodyMode = document.body?.getAttribute('data-mode');
    if (bodyMode === 'player' || bodyMode === 'playground' || bodyMode === 'playgroundSingle' || bodyMode === 'single') {
        return bodyMode as any;
    }

    const pathname = (typeof window !== 'undefined' ? window.location.pathname : '').toLowerCase();
    if (pathname.includes('player.html') || pathname.endsWith('/player') || pathname.endsWith('/player/')) {
        return 'player';
    }

    return 'playground';
}

export async function initPlayer(): Promise<void> {
    console.log('[ginga-node] INFO: initPlayer() starting...');
    await ensureCatalogLoaded();
    document.body.classList.add('mode-player');
    const appEl = document.getElementById('app');
    if (!appEl) {
        console.error('[ginga-node] #app element not found');
        return;
    }

    const search = getEffectiveSearch();
    const urlParams = new URLSearchParams(search);
    const appParam = urlParams.get('app');
    const playerParam = urlParams.get('player');

    const playerBaseUrl = resolvePlayerBaseUrl(playerParam);
    const targetExample = await resolveTargetExample(appParam);
    await loadExampleFiles(targetExample);

    const allFiles = getAllFiles(targetExample);

    const appUrl = (appParam && (appParam.startsWith('http://') || appParam.startsWith('https://')))
        ? appParam
        : getExampleUrl(targetExample);
    syncIframeStorage(window, targetExample);

    const iframe = document.createElement('iframe');
    iframe.id = 'preview-frame';
    iframe.style.cssText = 'width:100%; height:100%; border:none; margin:0; padding:0; display:block;';

    iframe.onload = () => {
        syncIframeStorage(iframe.contentWindow, targetExample);
    };

    const srcUrl = `${playerBaseUrl}?app=${encodeURIComponent(appUrl)}`;
    console.log('[ginga-node] INFO: Mounting inner player iframe src:', srcUrl);

    iframe.src = srcUrl;
    appEl.replaceChildren(iframe);
}

function buildExampleOptions(): string {
    const groups: Record<string, { key: string; label: string }[]> = {
        'General': [],
        'General HTML': [],
        'Embedded': [],
        'Primeiro João': [],
        'Multiuser': [],
        'Lua': [],
        'HTML': []
    };

    for (const [key, ex] of Object.entries(examples)) {
        if (key === 'uploaded') continue;
        const cat = (ex.category || '').toLowerCase();
        let groupName = 'General';
        let label = ex.mainFile;

        if (cat === 'primeiro-joao') {
            groupName = 'Primeiro João';
            label = ex.mainFile.includes('/') ? ex.mainFile.split('/').pop()! : ex.mainFile;
        } else if (cat === 'embedded') {
            groupName = 'Embedded';
        } else if (cat === 'multiuser') {
            groupName = 'Multiuser';
            label = `${key}/${ex.mainFile.split('/').pop()}`;
        } else if (cat === 'lua') {
            groupName = 'Lua';
        } else if (cat === 'general_html' || cat === 'general-html' || cat === 'general_thml') {
            groupName = 'General HTML';
        } else if (cat === 'html') {
            groupName = 'HTML';
        } else if (cat === 'general') {
            groupName = 'General';
            label = key === 'sbtvd' ? `${key}/${ex.mainFile.split('/').pop()}` : (ex.mainFile.includes('/') ? ex.mainFile.split('/').pop()! : ex.mainFile);
        } else if (cat) {
            groupName = cat.split(/[-_]/).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
        }

        if (!groups[groupName]) {
            groups[groupName] = [];
        }
        groups[groupName].push({ key, label });
    }

    return Object.entries(groups)
        .filter(([_, items]) => items.length > 0)
        .map(([groupName, items]) => {
            const opts = items.map(it => `            <option value="${it.key}">${it.label}</option>`).join('\n');
            return `          <optgroup label="${groupName}">\n${opts}\n          </optgroup>`;
        })
        .join('\n');
}

export async function initPlayground(mode: string): Promise<void> {
    console.log('[ginga-node] INFO: initPlayground() starting in mode:', mode);
    await ensureCatalogLoaded();

    let appEl = document.getElementById('app');
    if (!appEl) {
        appEl = document.createElement('div');
        appEl.id = 'app';
        document.body.appendChild(appEl);
    }

    if (!document.getElementById('editor-container')) {
        appEl.innerHTML = `
    <header class="playground-header">
      <div class="header-left" style="display: flex; align-items: center; gap: 20px;">
        <div class="logo">Ginga Playground</div>
        <div class="controls">
          <label for="example-select" style="font-size: 0.9rem; color: #ccc;">Select example:</label>
          <select id="example-select">
${buildExampleOptions()}
          </select>
          <button id="run-btn">Run</button>
          <button id="upload-btn">Upload Files</button>
          <input type="file" id="file-input" multiple style="display: none;"
            accept=".ncl,.xml,.lua,.html,.js,.css,.txt,.png,.jpg,.jpeg,.mp4,.mp3,.gif,.webp" />
        </div>
      </div>
      <div class="header-right" style="font-size: 0.8rem; color: #aaa; display: flex; align-items: center; gap: 5px;">
        Powered by <a href="https://github.com/ginga-org-br/gingaf" target="_blank" rel="noopener noreferrer"
          style="color: #58a6ff; text-decoration: none; display: flex; align-items: center; gap: 5px;">
          gingaf
          <svg height="16" aria-hidden="true" viewBox="0 0 16 16" version="1.1" width="16" data-view-component="true"
            fill="currentColor">
            <path
              d="M8 0c4.42 0 8 3.58 8 8a8.013 8.013 0 0 1-5.45 7.59c-.4.08-.55-.17-.55-.38 0-.27.01-1.13.01-2.2 0-.75-.25-1.23-.54-1.48 1.78-.2 3.65-.88 3.65-3.95 0-.88-.31-1.59-.82-2.15.08-.2.36-1.02-.08-2.12 0 0-.67-.22-2.2.82-.64-.18-1.32-.27-2-.27-.68 0-1.36.09-2 .27-1.53-1.03-2.2-.82-2.2-.82-.44 1.1-.16 1.92-.08 2.12-.51.56-.82 1.28-.82 2.15 0 3.06 1.86 3.75 3.64 3.95-.23.2-.44.55-.51 1.07-.46.21-1.61.55-2.33-.66-.15-.24-.6-.83-1.23-.82-.67.01-.27.38.01.53.34.19.73.9.82 1.13.16.45.68 1.31 2.69.94 0 .67.01 1.3.01 1.49 0 .21-.15.45-.55.38A7.995 7.995 0 0 1 0 8c0-4.42 3.58-8 8-8Z">
            </path>
          </svg>
        </a>
      </div>
    </header>
    <main class="split-pane">
      <div id="editor-wrapper">
        <div id="editor-tabs"></div>
        <div id="editor-container"></div>
        <div id="editor-overlay" class="hidden">
          <span>Editing blocked while running. Click Stop to edit.</span>
        </div>
      </div>
      <div id="preview-container">
        <iframe id="preview-frame" src="about:blank" frameborder="0"></iframe>
      </div>
    </main>`;
    }

    const editorContainer = document.getElementById('editor-container');
    const editorTabs = document.getElementById('editor-tabs');
    const runBtn = document.getElementById('run-btn');
    const uploadBtn = document.getElementById('upload-btn');
    const fileInput = document.getElementById('file-input') as HTMLInputElement;
    const selectEl = document.getElementById('example-select') as HTMLSelectElement;
    const iframe = document.getElementById('preview-frame') as HTMLIFrameElement;

    if (!editorContainer || !editorTabs || !runBtn || !selectEl || !iframe) {
        console.error('[ginga-node] Required Playground DOM elements not found');
        return;
    }

    const search = getEffectiveSearch();
    const urlParams = new URLSearchParams(search);
    const appParam = urlParams.get('app') || urlParams.get('example');
    const playerParam = urlParams.get('player');
    const isEmbed = urlParams.get('embed') === 'true' || mode === 'single' || mode === 'playgroundSingle';

    const playerBaseUrl = resolvePlayerBaseUrl(playerParam);

    if (isEmbed) {
        document.body.classList.add('mode-single');
        document.body.classList.add('embed-mode');
        const exampleSelect = document.getElementById('example-select');
        if (exampleSelect) exampleSelect.style.display = 'none';
        const selectLabel = document.querySelector('label[for="example-select"]') as HTMLElement;
        if (selectLabel) selectLabel.style.display = 'none';
        if (uploadBtn) uploadBtn.style.display = 'none';
    }

    let currentExample = await resolveTargetExample(appParam);
    let currentFileName = currentExample.mainFile;
    let isRunning = false;

    const foundKey = Object.keys(examples).find(k => examples[k] === currentExample);
    if (foundKey && selectEl) {
        selectEl.value = foundKey;
    }

    await loadExampleFiles(currentExample);

    const editor = monaco.editor.create(editorContainer, {
        value: currentExample.files[currentFileName] || '',
        language: getEditorLanguage(currentFileName),
        theme: 'vs-dark',
        minimap: { enabled: false },
        automaticLayout: true,
        wordWrap: 'on',
    });

    const renderTabs = () => {
        editorTabs.innerHTML = '';
        const renderedNames = new Set<string>();
        for (const fileName of Object.keys(currentExample.files)) {
            if (!isEditableFile(fileName)) continue;
            const tabName = fileName.includes('/') ? fileName.split('/').pop()! : fileName;
            if (renderedNames.has(tabName)) continue;
            renderedNames.add(tabName);
            const tab = document.createElement('div');
            tab.className = 'tab' + (fileName === currentFileName ? ' active' : '');
            tab.textContent = tabName;
            tab.addEventListener('click', () => {
                if (!isRunning) {
                    currentExample.files[currentFileName] = editor.getValue();
                    currentFileName = fileName;
                    editor.setValue(currentExample.files[currentFileName] || '');
                    monaco.editor.setModelLanguage(editor.getModel()!, getEditorLanguage(fileName));
                    renderTabs();
                }
            });
            editorTabs.appendChild(tab);
        }
    };

    renderTabs();

    selectEl.addEventListener('change', async () => {
        const selected = selectEl.value;
        if (examples[selected]) {
            if (isRunning) {
                runBtn.click();
            }
            currentExample = examples[selected];
            await loadExampleFiles(currentExample);
            currentFileName = currentExample.mainFile;
            editor.setValue(currentExample.files[currentFileName] || '');
            monaco.editor.setModelLanguage(editor.getModel()!, getEditorLanguage(currentFileName));
            renderTabs();
        }
    });

    if (uploadBtn && fileInput) {
        uploadBtn.addEventListener('click', () => {
            if (!isRunning) {
                fileInput.click();
            }
        });

        fileInput.addEventListener('change', async () => {
            const files = fileInput.files;
            if (!files || files.length === 0) return;

            if (!examples['uploaded']) {
                examples['uploaded'] = {
                    mainFile: '',
                    files: {}
                };
            }

            let mainFileCandidate = '';
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                if (isEditableFile(file.name)) {
                    const text = await file.text();
                    examples['uploaded'].files[file.name] = text;
                } else {
                    const url = URL.createObjectURL(file);
                    examples['uploaded'].files[file.name] = url;
                }

                if (file.name.endsWith('.ncl')) {
                    mainFileCandidate = file.name;
                } else if (file.name.endsWith('.html') && !mainFileCandidate.endsWith('.ncl')) {
                    mainFileCandidate = file.name;
                } else if (!mainFileCandidate && isEditableFile(file.name)) {
                    mainFileCandidate = file.name;
                }
            }

            if (mainFileCandidate) {
                examples['uploaded'].mainFile = mainFileCandidate;
            }

            let uploadedOption = Array.from(selectEl.options).find(opt => opt.value === 'uploaded');
            if (!uploadedOption) {
                uploadedOption = document.createElement('option');
                uploadedOption.value = 'uploaded';
                uploadedOption.textContent = 'Uploaded Files';
                selectEl.appendChild(uploadedOption);
            }

            selectEl.value = 'uploaded';
            selectEl.dispatchEvent(new Event('change'));
            fileInput.value = '';
        });
    }

    iframe.src = 'about:blank';

    runBtn.addEventListener('click', async () => {
        if (isRunning) {
            editor.updateOptions({ readOnly: false });
            document.getElementById('editor-overlay')?.classList.add('hidden');
            runBtn.textContent = 'Run';
            iframe.src = 'about:blank';
            try {
                sessionStorage.removeItem('GINGA_APP');
                sessionStorage.removeItem('GINGA_PLAYGROUND_MAIN');
                sessionStorage.removeItem('GINGA_PLAYGROUND_FILES');
                if ((window as any).GingaApp) {
                    (window as any).GingaApp.appPath = null;
                    (window as any).GingaApp.currentApp = null;
                }
            } catch (_) { }
            isRunning = false;
        } else {
            currentExample.files[currentFileName] = editor.getValue();
            const appUrl = getExampleUrl(currentExample);
            syncIframeStorage(window, currentExample);

            editor.updateOptions({ readOnly: true });
            document.getElementById('editor-overlay')?.classList.remove('hidden');
            runBtn.textContent = 'Stop';

            const srcUrl = `${playerBaseUrl}?app=${encodeURIComponent(appUrl)}`;
            iframe.src = srcUrl;
            iframe.onload = () => {
                syncIframeStorage(iframe.contentWindow, currentExample);
            };
            isRunning = true;
        }
    });
}

const mode = getEffectiveMode();
console.log('[ginga-node] INFO: main.ts router dispatched mode:', mode);

if (mode === 'player') {
    initPlayer();
} else {
    initPlayground(mode);
}
